import { useState, useEffect, useCallback, useRef } from 'react';
import { Person } from './types';
import { Team, TeamPosition, TeamStrength, TeamCandidate, CandidateSource } from './types/team';
import { peopleApi, API_BASE } from './services/api';
import * as teamApi from './services/teamApi';
import { PersonCard } from './components/People/PersonCard';
import { SearchContextPanelProps } from './components/People/SearchContextPanel';
import { CandidateProfileModal } from './components/People/CandidateProfileModal';
import { AIResultCard } from './components/People/AIResultCard';
import { LoginScreen } from './components/Auth/LoginScreen';
import { useAuth, clearSessionIf401 } from './hooks/useAuth';
import { ChatWindow, ChatMode, ChatWindowHandle } from './components/Chat/ChatWindow';
import { AdminSettings } from './components/Admin/AdminSettings';
import { shouldDriveListFromAiSearch } from './search/aiSearchListState';
import TeamComposerBar from './components/Teams/TeamComposerBar';
import { PositionEditDialog } from './components/Teams/PositionEditDialog';
import { LeftPanel } from './components/LeftPanel/LeftPanel';
import { TeamDialog } from './components/LeftPanel/TeamDialog';
import { ProjectDialog } from './components/LeftPanel/ProjectDialog';
import { ShareTeamModal } from './components/LeftPanel/ShareTeamModal';
import { AISearchCriteriaBar, type AISearchCriteriaPayload } from './components/People/AISearchCriteriaBar';
import { useProjectStore } from './stores/projectStore';
import { CandidatePoolsPage } from './components/CandidatePools/CandidatePoolsPage';
import { Footer } from './components/Layout/Footer';

const AUTH_MODE = (import.meta.env.VITE_AUTH_MODE || 'google').toLowerCase(); // 'none', 'google'
const AUTH_ENABLED = AUTH_MODE !== 'none';

type PageType = 'main' | 'admin' | 'candidate_pools';

// Position suggestion from AI
interface PositionSuggestion {
  id: number;
  role: string;
  count_needed: number;
  skills_required: string[];
  priority: 'critical' | 'important' | 'nice-to-have';
  reasoning?: string;
  status: 'proposed' | 'confirmed';
  source: 'ai' | 'user';
}

// Candidate match from AI
interface CandidateMatch {
  person_id: number;
  position_index: number; // Index in positions array
  /** @deprecated Use scores instead */
  match_score: number;
  scores?: {
    /** Heuristic skills / profile overlap (search & team flows) */
    skillMatch: number;
    /** Batch LLM semantic score when present (chat search); team flows use same field */
    semanticMatch: number;
    teamFitBonus: number; // 0-50, cross-position value
  };
  match_reasoning: string;
  profile?: {
    name: string;
    title: string;
    team: string;
    expertise: string;
    linkedinProfileUrl?: string | null;
  };
}

function App() {
  const { isAuthenticated, user, token, isAdmin: isAdminFromStore } = useAuth();
  const [currentPage, setCurrentPage] = useState<PageType>(() => {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/candidate-pools')) {
      return 'candidate_pools';
    }
    return 'main';
  });
  const [candidatePoolId, setCandidatePoolId] = useState<number | null>(() => {
    if (typeof window !== 'undefined') {
      const m = window.location.pathname.match(/^\/candidate-pools\/(\d+)/);
      return m ? parseInt(m[1], 10) : null;
    }
    return null;
  });
  const [showLogin, setShowLogin] = useState(false);
  const [netlifyUser] = useState<any>(null);
  const [showChat, setShowChat] = useState(true); // Open by default
  const [showLeftPanel, setShowLeftPanel] = useState(false); // Hidden by default in search-focused flow
  const [leftPanelPeeking, setLeftPanelPeeking] = useState(false); // Hover-to-peek overlay mode
  const peekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peekLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Auto-collapse left panel when user interacts with other panels
  const collapseLeftPanel = useCallback(() => {
    setShowLeftPanel(false);
    setLeftPanelPeeking(false);
    if (peekTimerRef.current) { clearTimeout(peekTimerRef.current); peekTimerRef.current = null; }
    if (peekLeaveTimerRef.current) { clearTimeout(peekLeaveTimerRef.current); peekLeaveTimerRef.current = null; }
  }, []);
  
  // Edge hover: start peek timer (~400ms delay)
  const handleEdgeMouseEnter = useCallback(() => {
    if (peekLeaveTimerRef.current) { clearTimeout(peekLeaveTimerRef.current); peekLeaveTimerRef.current = null; }
    peekTimerRef.current = setTimeout(() => {
      setLeftPanelPeeking(true);
    }, 400);
  }, []);
  
  // Edge hover: cancel peek timer
  const handleEdgeMouseLeave = useCallback(() => {
    if (peekTimerRef.current) { clearTimeout(peekTimerRef.current); peekTimerRef.current = null; }
  }, []);
  
  // Peek panel: mouse entered the panel overlay (cancel any leave timer)
  const handlePeekPanelEnter = useCallback(() => {
    if (peekLeaveTimerRef.current) { clearTimeout(peekLeaveTimerRef.current); peekLeaveTimerRef.current = null; }
  }, []);
  
  // Peek panel: mouse left the panel overlay (hide after brief delay for tolerance)
  const handlePeekPanelLeave = useCallback(() => {
    peekLeaveTimerRef.current = setTimeout(() => {
      setLeftPanelPeeking(false);
    }, 300);
  }, []);
  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
      if (peekLeaveTimerRef.current) clearTimeout(peekLeaveTimerRef.current);
    };
  }, []);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState<any>(null);
  const [editingProject, setEditingProject] = useState<any>(null);
  const [shareTeamId, setShareTeamId] = useState<number | null>(null);
  const [shareTeamName, setShareTeamName] = useState('');
  const [allPeople, setAllPeople] = useState<Person[]>([]);
  const [filteredPeople, setFilteredPeople] = useState<Person[]>([]);
  const [aiMatches, setAiMatches] = useState<any[]>([]);
  const [aiSearchCriteria, setAiSearchCriteria] = useState<AISearchCriteriaPayload | null>(null);
  /** True while chat search pipeline is still resolving people matches (search chat mode only). */
  const [aiSearchPanelBusy, setAiSearchPanelBusy] = useState(false);
  const [isAIMode, setIsAIMode] = useState(true); // Search-first default
  const [chatMode, setChatMode] = useState<ChatMode>('search');
  const [chatModeLockedByUser, setChatModeLockedByUser] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterByPositionId, setFilterByPositionId] = useState<number | null>(null); // Filter people by position skills

  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [_modalSearchContext, setModalSearchContext] = useState<SearchContextPanelProps['searchContext'] | null>(null);
  // Phase 3F — candidate pool review queue. `null` means modal is not in queue
  // mode; when set, the modal renders the header stepper + footer action bar.
  const [reviewQueue, setReviewQueue] = useState<{ ids: number[]; index: number } | null>(null);
  const candidatePoolRefreshRef = useRef<(() => void) | null>(null);
  const [loading, setLoading] = useState(true);

  /** AI/SSE payloads may carry a thin `person`; merge roster row so the modal has full profile fields. */
  const mergePersonWithAllPeople = useCallback(
    (p: Person): Person => {
      if (p.id == null) return p;
      const full = allPeople.find((x) => x.id === p.id);
      if (!full) return p;
      // Roster row first, then `p` wins on overlapping keys so PATCH/API updates
      // (e.g. Contact, LinkedInProfileUrl) are not overwritten by stale `allPeople`
      // before the parent state refresh commits.
      return { ...full, ...p };
    },
    [allPeople]
  );

  /** Last AI search context for this person, if they appear in current results (works in Browse too). */
  const buildModalSearchContextForPerson = useCallback(
    (person: Person): SearchContextPanelProps['searchContext'] | null => {
      if (!aiSearchCriteria || !Array.isArray(aiMatches) || aiMatches.length === 0) return null;
      const aiMatch = aiMatches.find(
        (m) =>
          (m.person?.id != null && person.id != null && m.person.id === person.id) ||
          (m.person?.Name && person.Name && m.person.Name === person.Name)
      );
      if (!aiMatch) return null;
      return {
        lexicalSearchQuery: aiSearchCriteria.searchQuery,
        ai_reasoning: aiMatch.ai_reasoning,
        semantic_insight: aiMatch.semantic_insight,
        match_score: aiMatch.match_score,
        scores: aiMatch.scores,
        strengths: aiMatch.strengths,
      };
    },
    [aiMatches, aiSearchCriteria]
  );
  const [_draggedPerson, setDraggedPerson] = useState<Person | null>(null);
  const chatWindowRef = useRef<ChatWindowHandle>(null);

  // ==========================================================================
  // TEAM COMPOSER STATE
  // ==========================================================================
  const [composerExpanded, setComposerExpanded] = useState(true);
  const [composerTeam, setComposerTeam] = useState<Team | null>(null);
  const [composerPositions, setComposerPositions] = useState<TeamPosition[]>([]);
  const [composerStrength, setComposerStrength] = useState<TeamStrength | null>(null);
  const [highlightedPositionId, setHighlightedPositionId] = useState<number | null>(null);
  const [editingPositionId, setEditingPositionId] = useState<number | null>(null);
  const [showPositionDialog, setShowPositionDialog] = useState(false);

  const projects = useProjectStore(s => s.projects);
  const prevTeamIdRef = useRef<number | null>(null);
  const [pendingProjectId, setPendingProjectId] = useState<number | null>(null);
  const effectiveProjectId = composerTeam?.project_id ?? pendingProjectId;
  const composerProject = effectiveProjectId != null
    ? projects.find(p => p.id === effectiveProjectId)
    : null;

  // Clear position highlight after a delay
  const handlePositionHighlight = useCallback((positionId: number | null) => {
    setHighlightedPositionId(positionId);
    if (positionId) {
      // Auto-clear highlight after 5 seconds
      setTimeout(() => {
        setHighlightedPositionId(prev => prev === positionId ? null : prev);
      }, 5000);
    }
  }, []);
  
  // Note: Layout now uses flexbox instead of dynamic pixel calculations

  // Sync left panel team data when composer positions change
  useEffect(() => {
    if (!composerTeam) return;
    const filledCount = composerPositions.reduce((sum, p) => {
      const selected = p.candidates?.filter(c => c.status === 'selected').length || 0;
      return sum + selected;
    }, 0);
    useProjectStore.getState().updateTeamInProject(composerTeam.id, {
      position_count: composerPositions.length,
      filled_count: filledCount,
    });

    // Also refresh quality score from backend (debounced)
    if (composerTeam.id > 0) {
      const timer = setTimeout(async () => {
        try {
          const { getTeamQuality } = await import('./services/projectApi');
          const quality = await getTeamQuality(composerTeam.id);
          useProjectStore.getState().updateTeamInProject(composerTeam.id, {
            quality_score: quality.score,
          });
        } catch (e) {
          // Quality check may fail for teams without full data
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [composerPositions, composerTeam?.id]);

  // Sync left panel when team strength updates (including reset to 0)
  useEffect(() => {
    if (!composerTeam) return;
    useProjectStore.getState().updateTeamInProject(composerTeam.id, {
      team_strength: composerStrength?.overall_score ?? 0,
    });
  }, [composerStrength, composerTeam?.id]);

  // ==========================================================================
  // TEAM COMPOSER HANDLERS
  // ==========================================================================

  // Helper: ensure a persistent team exists, auto-creating one if needed.
  // Returns the team (with a real positive ID). Updates state + left panel.
  const ensureTeamExists = useCallback(async (nameOverride?: string): Promise<Team> => {
    // Already have a saved team
    if (composerTeam?.id && composerTeam.id > 0) {
      return composerTeam;
    }

    // Generate auto-name: "Team YYYY-MM-DD-HH24-MI"
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const autoName = nameOverride || composerTeam?.name || 
      `Team ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}-${pad(now.getMinutes())}`;

    const newTeam = await teamApi.createTeam({
      name: autoName,
      project_id: composerTeam?.project_id ?? pendingProjectId ?? undefined,
      status: 'planning',
      skills_required: ['general'],
      member_count_desired: 5,
      description: 'Created via Team Composer',
    });

    setComposerTeam(newTeam);
    if (pendingProjectId != null) setPendingProjectId(null);
    // Refresh left panel so the new team appears immediately
    useProjectStore.getState().loadProjects();
    console.log(`✅ Auto-created team: "${newTeam.name}" (ID: ${newTeam.id})`);
    return newTeam;
  }, [composerTeam, pendingProjectId]);

  // Handle AI position suggestions from chat
  const handlePositionsSuggested = useCallback((suggestions: PositionSuggestion[], teamId?: number) => {
    console.log('📋 AI suggested positions:', suggestions, 'for team:', teamId);
    
    // Backend already sends formatted positions, just need to add candidates array
    const newPositions: TeamPosition[] = suggestions.map((s, index) => ({
      id: s.id || -(index + 1), // Use provided ID or generate temporary
      team_id: teamId || composerTeam?.id || -1,
      role: s.role,
      count_needed: s.count_needed,
      skills_required: s.skills_required,
      priority: s.priority,
      status: (s.status as 'proposed' | 'confirmed') || 'proposed',
      source: (s.source as 'ai' | 'user') || 'ai',
      reasoning: s.reasoning,
      created_at: new Date().toISOString(),
      candidates: [],
    }));

    setComposerPositions(prev => {
      // Dedupe: only add positions that don't already exist (by ID or by role for same team)
      const existingIds = new Set(prev.map(p => p.id));
      const existingRoles = new Set(prev.filter(p => p.team_id === teamId).map(p => p.role.toLowerCase()));
      
      const uniqueNewPositions = newPositions.filter(p => {
        // Skip if ID already exists (position was already created)
        if (existingIds.has(p.id)) {
          console.log(`⏭️ Skipping duplicate position ID: ${p.id}`);
          return false;
        }
        // Skip if same role already exists for this team
        if (existingRoles.has(p.role.toLowerCase())) {
          console.log(`⏭️ Skipping duplicate role: ${p.role} for team ${teamId}`);
          return false;
        }
        return true;
      });
      
      if (uniqueNewPositions.length === 0) {
        console.log('📋 No new unique positions to add');
        return prev;
      }
      
      console.log(`📋 Adding ${uniqueNewPositions.length} unique positions`);
      return [...prev, ...uniqueNewPositions];
    });
    
    // Auto-expand composer when positions are suggested
    setComposerExpanded(true);
  }, [composerTeam]);

  // Handle AI candidate matches from chat
  // NOTE: This does NOT auto-add candidates to positions.
  // Candidates are displayed in chat with "+Add" buttons.
  // User clicks "+Add" to add them via handleAddCandidateFromChat.
  const handleCandidatesMatched = useCallback((matches: CandidateMatch[], teamId?: number) => {
    console.log('👥 AI matched candidates (for chat display only):', matches.length, 'candidates for team:', teamId);
    // Candidates are shown in chat via the Message.candidates property
    // The actual adding to positions happens when user clicks "+Add" button
    // which calls handleAddCandidateFromChat
  }, []);

  // Handle team strength update from chat
  const handleTeamStrengthUpdate = useCallback((strength: TeamStrength, teamId?: number) => {
    console.log('💪 Team strength updated:', strength, 'for team:', teamId);
    setComposerStrength(strength);
  }, []);

  // ==========================================================================
  // AUTO-CALCULATE TEAM STRENGTH WHEN CANDIDATES CHANGE
  // ==========================================================================
  // This provides real-time feedback as users add/remove candidates
  useEffect(() => {
    if (composerPositions.length === 0) {
      setComposerStrength(null);
      return;
    }

    // Calculate strength based on positions and candidates
    let totalSlots = 0;
    let filledSlots = 0;
    let totalMatchScore = 0;
    let candidatesWithScores = 0;
    const gaps: string[] = [];
    const recommendations: string[] = [];

    // Helper function to calculate overall match between candidate and position
    // Uses three-tier scores if available, falls back to legacy match_score
    const calculateSkillMatch = (candidate: TeamCandidate, position: typeof composerPositions[0]): number => {
      // If AI provided three-tier scores, calculate normalized base + additive bonus
      if (candidate.scores) {
        const { skillMatch, semanticMatch, teamFitBonus } = candidate.scores;
        // Base score normalized to 1.0: skills 40%, semantic 60%
        // Team fit bonus is additive on top (0-50 range)
        const baseScore = (skillMatch * 0.4) + (semanticMatch * 0.6);
        return Math.min(100, Math.round(baseScore + teamFitBonus));
      }
      
      // If AI already provided a legacy match score, use it
      if (candidate.match_score !== undefined && candidate.match_score !== null) {
        return candidate.match_score;
      }
      
      // If position has no required skills, default to moderate match
      const requiredSkills = position.skills_required || [];
      const niceToHaveSkills = position.skills_nice_to_have || [];
      if (requiredSkills.length === 0 && niceToHaveSkills.length === 0) {
        return 70; // No skills to match against
      }
      
      // Get candidate's skills from their profile
      const profile = candidate.profile;
      if (!profile) {
        return 30; // No profile info = poor match
      }
      
      // Combine all candidate skill-related fields
      const candidateSkills = [
        profile.title || '',
        profile.expertise || '',
        (profile as any).additionalSkills || '',
        (profile as any).aiToolExperience || '',
        (profile as any).jobResponsibilities || ''
      ].join(' ').toLowerCase();
      
      // Count how many required skills the candidate has
      const requiredMatchCount = requiredSkills.filter(skill => 
        candidateSkills.includes(skill.toLowerCase())
      ).length;
      
      // Count nice-to-have matches (partial bonus)
      const niceMatchCount = niceToHaveSkills.filter(skill =>
        candidateSkills.includes(skill.toLowerCase())
      ).length;
      const niceBonus = niceToHaveSkills.length > 0
        ? Math.round((niceMatchCount / niceToHaveSkills.length) * 15)
        : 0;
      
      // Calculate match percentage
      const basePercent = requiredSkills.length > 0
        ? Math.round((requiredMatchCount / requiredSkills.length) * 100)
        : 50;
      
      return Math.min(100, basePercent + niceBonus);
    };

    composerPositions.forEach(position => {
      const needed = position.count_needed || 1;
      totalSlots += needed;
      
      // Count all active candidates (both selected and proposed)
      const activeCandidates = (position.candidates || []).filter(
        c => c.status === 'selected' || c.status === 'proposed'
      );
      const filled = Math.min(activeCandidates.length, needed);
      filledSlots += filled;
      
      // Calculate match scores for all candidates (either from AI or computed)
      activeCandidates.forEach(candidate => {
        const matchScore = calculateSkillMatch(candidate, position);
        totalMatchScore += matchScore;
        candidatesWithScores++;
      });
      
      // Track gaps
      if (filled < needed) {
        gaps.push(`${position.role}: ${needed - filled} more needed`);
      }
    });

    // Calculate coverage score (how many slots are filled)
    const coverageScore = totalSlots > 0 ? (filledSlots / totalSlots) * 100 : 0;
    
    // Calculate quality score (average match score across all candidates)
    // We now calculate match scores for ALL candidates (AI or skill-based)
    const qualityScore = candidatesWithScores > 0 
      ? (totalMatchScore / candidatesWithScores) 
      : 0;
    
    // Overall score: balanced mix of coverage and quality
    // Both matter equally - you need the right people in the right roles
    const overallScore = Math.round(coverageScore * 0.5 + qualityScore * 0.5);

    // Generate recommendations
    if (filledSlots === 0) {
      recommendations.push('Add candidates to positions to build your team');
    } else if (gaps.length > 0) {
      recommendations.push(`Fill remaining positions: ${gaps.length} position(s) need more candidates`);
    }
    if (qualityScore < 50 && filledSlots > 0) {
      recommendations.push('Some candidates may not match position requirements - review skill alignment');
    }

    const newStrength: TeamStrength = {
      overall_score: overallScore,
      per_skill: {},
      member_scores: [],
      gaps,
      team_reasoning: recommendations.join('. '),
    };

    setComposerStrength(newStrength);
  }, [composerPositions]);

  // Handle team created from chat (new team auto-created)
  const handleTeamCreated = useCallback(async (teamEvent: { id: number; name: string; description?: string; isNew: boolean }) => {
    console.log('✨ Team created:', teamEvent);
    
    const newTeam: Team = {
      id: teamEvent.id,
      name: teamEvent.name,
      project_id: pendingProjectId,
      description: teamEvent.description,
      status: 'planning',
      skills_required: [],
      member_count_desired: 5,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    if (pendingProjectId != null) {
      teamApi.updateTeam(teamEvent.id, { project_id: pendingProjectId } as any).catch(() => {});
      setPendingProjectId(null);
    }
    
    setComposerTeam(newTeam);
    // Clear positions for new team - they'll be added via positions_suggested event
    setComposerPositions([]);
    setComposerStrength(null);
    setComposerExpanded(true); // Auto-expand
    // Reload left panel to show new team
    useProjectStore.getState().loadProjects();
    
    // Note: We DON'T load positions from backend here - let positions_suggested SSE event handle it
    // Loading here would cause a race condition where async fetch overwrites SSE-provided positions
    // Instead, we'll load positions from backend only as a delayed fallback
    if (teamEvent.id > 0) {
      // Delayed fallback: if no positions received via SSE within 3s, load from backend
      setTimeout(async () => {
        try {
          const positions = await teamApi.getTeamPositions(teamEvent.id);
          if (positions.length > 0) {
            setComposerPositions(prev => {
              if (prev.length === 0) {
                console.log(`📋 Loaded ${positions.length} positions from backend (fallback)`);
                return positions;
              }
              return prev; // SSE already delivered, keep those
            });
          } else {
            console.log('⏰ No positions received via SSE and backend has none - positions may still be processing');
          }
        } catch (err) {
          console.error('Failed to load positions fallback:', err);
        }
      }, 3000);
    }
  }, []);

  // Handle team selected from chat (existing team matched)
  const handleTeamSelected = useCallback(async (teamEvent: { id: number; name: string; isNew: boolean }) => {
    console.log('📌 Team selected:', teamEvent);
    
    // Load the existing team details
    try {
      const existingTeam = await teamApi.getTeam(teamEvent.id);
      setComposerTeam(existingTeam);
      
      // Load existing positions for this team
      const positions = await teamApi.getTeamPositions(teamEvent.id);
      setComposerPositions(positions);
      
      setComposerExpanded(true); // Auto-expand
    } catch (error) {
      console.error('Failed to load selected team:', error);
      // Fallback to basic team info
      setComposerTeam({
        id: teamEvent.id,
        name: teamEvent.name,
        status: 'planning',
        skills_required: [],
        member_count_desired: 5,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
  }, []);

  // Add a new position (user-created) — auto-creates team if needed
  const handleAddPosition = useCallback(async (position: Partial<TeamPosition>) => {
    try {
      const team = await ensureTeamExists();

      const savedPosition = await teamApi.createPosition(team.id, {
        role: position.role || 'New Position',
        count_needed: position.count_needed || 1,
        skills_required: position.skills_required || [],
        priority: position.priority || 'important',
        status: 'confirmed',
        source: 'user',
        reasoning: position.reasoning,
      });
      setComposerPositions(prev => [...prev, savedPosition]);
    } catch (error) {
      console.error('Failed to create position:', error);
      // Fallback: add locally
      const newPosition: TeamPosition = {
        id: -(Date.now()),
        team_id: composerTeam?.id || -1,
        role: position.role || 'New Position',
        count_needed: position.count_needed || 1,
        skills_required: position.skills_required || [],
        priority: position.priority || 'important',
        status: 'confirmed',
        source: 'user',
        reasoning: position.reasoning,
        created_at: new Date().toISOString(),
        candidates: [],
      };
      setComposerPositions(prev => [...prev, newPosition]);
    }
  }, [composerTeam, ensureTeamExists]);

  // ==========================================================================
  // HELPER: Calculate skill match between a person and a position
  // Returns three-tier scores for frontend use
  // ==========================================================================
  const calculatePersonPositionMatch = useCallback((person: Person, position: TeamPosition): { 
    score: number; 
    scores: { skillMatch: number; semanticMatch: number; teamFitBonus: number };
    reasoning: string;
  } => {
    const requiredSkills = position.skills_required || [];
    const niceToHaveSkills = position.skills_nice_to_have || [];
    
    // If no skills required, moderate default match
    if (requiredSkills.length === 0 && niceToHaveSkills.length === 0) {
      return { 
        score: 70, 
        scores: { skillMatch: 70, semanticMatch: 70, teamFitBonus: 0 },
        reasoning: 'No specific skills required for this position' 
      };
    }
    
    // Combine all person's skill-related fields
    const personSkills = [
      person.Title || '',
      person.Expertise || '',
      person.AdditionalSkills || '',
      person.AIToolExperience || '',
      person.ProfessionalInterests || '',
      person.JobResponsibilities || ''
    ].join(' ').toLowerCase();
    
    // Count matching required skills
    const matchedRequired = requiredSkills.filter(skill => 
      personSkills.includes(skill.toLowerCase())
    );
    const requiredMatchCount = matchedRequired.length;
    const requiredTotal = requiredSkills.length || 1;
    const skillMatch = Math.round((requiredMatchCount / requiredTotal) * 100);
    
    // Count matching nice-to-have skills (partial credit: up to 15 bonus points)
    const matchedNiceToHave = niceToHaveSkills.filter(skill => 
      personSkills.includes(skill.toLowerCase())
    );
    const niceToHaveBonus = niceToHaveSkills.length > 0
      ? Math.round((matchedNiceToHave.length / niceToHaveSkills.length) * 15)
      : 0;
    
    // For frontend-only calculation, semantic match considers role + description + responsibilities + experience level
    // Real semantic match comes from backend AI
    let semanticBonus = 0;
    // Role title match
    if (position.role.toLowerCase().split(' ').some(word => 
      word.length > 2 && (person.Title || '').toLowerCase().includes(word)
    )) {
      semanticBonus += 25;
    }
    // Description match
    if (position.description) {
      const descWords = position.description.toLowerCase().split(/\s+/).filter(w => w.length > 3);
      const descMatches = descWords.filter(w => personSkills.includes(w)).length;
      semanticBonus += Math.min(15, Math.round((descMatches / Math.max(descWords.length, 1)) * 30));
    }
    // Responsibilities match
    if (position.responsibilities?.length) {
      const respText = position.responsibilities.join(' ').toLowerCase();
      const respWords = respText.split(/\s+/).filter(w => w.length > 3);
      const respMatches = respWords.filter(w => personSkills.includes(w)).length;
      semanticBonus += Math.min(10, Math.round((respMatches / Math.max(respWords.length, 1)) * 20));
    }
    // Experience level match (simple heuristic)
    if (position.experience_level) {
      const titleLower = (person.Title || '').toLowerCase();
      if (position.experience_level === 'senior' && (titleLower.includes('senior') || titleLower.includes('lead') || titleLower.includes('principal'))) {
        semanticBonus += 10;
      } else if (position.experience_level === 'mid' && !titleLower.includes('junior') && !titleLower.includes('intern')) {
        semanticBonus += 5;
      } else if (position.experience_level === 'junior' && (titleLower.includes('junior') || titleLower.includes('associate'))) {
        semanticBonus += 10;
      }
    }
    const semanticMatch = Math.min(100, skillMatch + semanticBonus + niceToHaveBonus);
    
    // Calculate team fit bonus: cross-position synergy
    // Check if candidate has skills useful for OTHER positions in the team
    let teamFitBonus = 0;
    const otherPositions = composerPositions.filter(p => p.id !== position.id);
    for (const otherPos of otherPositions) {
      const otherSkills = [...(otherPos.skills_required || []), ...(otherPos.skills_nice_to_have || [])];
      const crossMatches = otherSkills.filter(s => personSkills.includes(s.toLowerCase()));
      if (crossMatches.length > 0) {
        teamFitBonus += Math.min(10, crossMatches.length * 5);
      }
    }
    teamFitBonus = Math.min(50, teamFitBonus); // Cap at 50
    
    // Calculate score: normalized base (sums to 1.0) + additive bonus
    const score = Math.min(100, Math.round(
      (skillMatch * 0.4) + (semanticMatch * 0.6) + teamFitBonus
    ));
    
    // Generate reasoning
// @ts-ignore
    const _allMatched = [...matchedRequired, ...matchedNiceToHave];
    let reasoning: string;
    if (requiredMatchCount === 0 && matchedNiceToHave.length === 0) {
      reasoning = `No matching skills found. Required: ${requiredSkills.join(', ')}`;
    } else if (requiredMatchCount === requiredSkills.length && requiredSkills.length > 0) {
      reasoning = `Perfect match! Has all required skills: ${matchedRequired.join(', ')}`;
      if (matchedNiceToHave.length > 0) {
        reasoning += ` + ${matchedNiceToHave.length} nice-to-have`;
      }
    } else {
      reasoning = `Matches ${requiredMatchCount}/${requiredSkills.length} required skills: ${matchedRequired.join(', ')}`;
      if (matchedNiceToHave.length > 0) {
        reasoning += ` + ${matchedNiceToHave.length} nice-to-have`;
      }
    }
    
    return { 
      score, 
      scores: { skillMatch, semanticMatch, teamFitBonus },
      reasoning 
    };
  }, [composerPositions]);

  // Create a position from a dropped person's skills and add them as candidate
  const handleCreatePositionFromPerson = useCallback(async (droppedPerson: {
    id: number;
    Name?: string;
    Title?: string;
    Expertise?: string;
    AdditionalSkills?: string;
  }) => {
    console.log('🏗️ Creating position from dropped person:', droppedPerson);

    // Extract skills from person's expertise
    const skills: string[] = [];
    
    if (droppedPerson.Expertise) {
      skills.push(...droppedPerson.Expertise.split(/[,;]/).map(s => s.trim()).filter(s => s).slice(0, 5));
    }
    
    if (droppedPerson.AdditionalSkills) {
      skills.push(...droppedPerson.AdditionalSkills.split(/[,;]/).map(s => s.trim()).filter(s => s).slice(0, 3));
    }

    // Use person's title as role name
    const role = droppedPerson.Title || 'New Position';

    // Create a temporary position ID
    const tempPositionId = -(Date.now());

    const newPosition: TeamPosition = {
      id: tempPositionId,
      team_id: composerTeam?.id || -1,
      role,
      count_needed: 1,
      skills_required: [...new Set(skills)].slice(0, 6), // Unique skills, max 6
      priority: 'important',
      status: 'confirmed',
      source: 'user',
      reasoning: `Created from ${droppedPerson.Name}'s profile`,
      created_at: new Date().toISOString(),
      candidates: [],
    };

    // Auto-create team if needed, then persist position
    let savedPositionId = tempPositionId;
    
    try {
      const team = await ensureTeamExists();
      const savedPosition = await teamApi.createPosition(team.id, {
        role: newPosition.role,
        count_needed: newPosition.count_needed,
        skills_required: newPosition.skills_required,
        priority: newPosition.priority,
        status: newPosition.status,
        source: 'user',
      });
      savedPositionId = savedPosition.id;
      newPosition.id = savedPosition.id;
      newPosition.team_id = savedPosition.team_id;
    } catch (error) {
      console.error('Failed to create position:', error);
    }

    // Add position to state
    setComposerPositions(prev => [...prev, newPosition]);

    // Now add the dropped person as a candidate to this new position
    // Find the full person data from allPeople
    const person = allPeople.find(p => p.id === droppedPerson.id);
    
    if (person) {
      // Calculate three-tier scores for this candidate
      const { score: matchScore, scores, reasoning: matchReasoning } = calculatePersonPositionMatch(person, newPosition);
      console.log(`📊 Match scores for ${person.Name} in ${newPosition.role}: 🧩${scores.skillMatch}% ✨${scores.semanticMatch}% 👥+${scores.teamFitBonus}%`);
      
      const newCandidate: TeamCandidate = {
        id: Date.now(),
        team_id: newPosition.team_id,
        position_id: savedPositionId,
        person_id: droppedPerson.id,
        status: 'selected',
        source: 'drag_drop',
        match_score: matchScore,
        scores, // Three-tier scores
        match_reasoning: matchReasoning,
        added_at: new Date().toISOString(),
        profile: {
          name: person.Name,
          title: person.Title,
          team: person.Team,
          expertise: person.Expertise,
        },
      };

      // Update the position with the candidate
      setComposerPositions(prev => prev.map(p => {
        if (p.id === savedPositionId || p.id === tempPositionId) {
          return { ...p, id: savedPositionId, candidates: [newCandidate] };
        }
        return p;
      }));

      // Persist candidate to backend (team is guaranteed to exist now)
      if (savedPositionId > 0) {
        try {
          const team = await ensureTeamExists();
          await teamApi.addCandidate(team.id, savedPositionId, {
            person_id: droppedPerson.id,
            source: 'drag_drop',
            status: 'selected',
          });
          console.log(`✅ Created position "${role}" and added ${person.Name} as candidate`);
        } catch (error) {
          console.error('Failed to persist candidate to backend:', error);
        }
      }
    }

    // Expand the composer to show the new position
    setComposerExpanded(true);
  }, [composerTeam, allPeople, calculatePersonPositionMatch, ensureTeamExists]);

  // Confirm a proposed position
  const handleConfirmPosition = useCallback(async (positionId: number) => {
    setComposerPositions(prev => prev.map(p => 
      p.id === positionId ? { ...p, status: 'confirmed' } : p
    ));

    // If saved team, persist to backend
    if (composerTeam?.id && composerTeam.id > 0 && positionId > 0) {
      try {
        await teamApi.confirmPosition(composerTeam.id, positionId);
      } catch (error) {
        console.error('Failed to confirm position:', error);
      }
    }
  }, [composerTeam]);

  // Delete a position
  const handleDeletePosition = useCallback(async (positionId: number) => {
    setComposerPositions(prev => prev.filter(p => p.id !== positionId));

    // If saved team and real position ID, delete from backend
    if (composerTeam?.id && composerTeam.id > 0 && positionId > 0) {
      try {
        await teamApi.deletePosition(composerTeam.id, positionId);
      } catch (error) {
        console.error('Failed to delete position:', error);
      }
    }
  }, [composerTeam]);

  // Open position edit dialog (edit existing)
  const handleEditPosition = useCallback((positionId: number) => {
    setEditingPositionId(positionId);
    setShowPositionDialog(true);
  }, []);

  // Open position edit dialog (create new)
  const handleNewPosition = useCallback(() => {
    setEditingPositionId(null);
    setShowPositionDialog(true);
  }, []);

  // Create position from edit dialog – returns the created position (with DB id)
  const handleCreatePosition = useCallback(async (data: Partial<TeamPosition>): Promise<TeamPosition> => {
    const team = await ensureTeamExists();
    if (!team.id || team.id <= 0) throw new Error('Failed to create team');

    const teamId = team.id;
    const created = await teamApi.createPosition(teamId, {
      role: data.role || 'New Position',
      count_needed: data.count_needed || 1,
      priority: data.priority || 'important',
      status: data.status,
      skills_required: data.skills_required,
      description: data.description,
      location_model: data.location_model,
      location: data.location,
      experience_level: data.experience_level,
      management_level: data.management_level,
      education_level: data.education_level,
      education_importance: data.education_importance,
      employment_type: data.employment_type,
      allocation_percentage: data.allocation_percentage,
      responsibilities: data.responsibilities,
      skills_nice_to_have: data.skills_nice_to_have,
      rate_country: data.rate_country,
      ai_analysis: data.ai_analysis,
    });
    const positionWithCandidates: TeamPosition = { ...created, candidates: [] };
    setComposerPositions(prev => [...prev, positionWithCandidates]);
    return positionWithCandidates;
  }, [composerTeam, ensureTeamExists]);

  // Update position from edit dialog (auto-save, partial fields)
  const handleUpdatePosition = useCallback(async (positionId: number, data: Partial<TeamPosition>): Promise<void> => {
    if (!composerTeam?.id || composerTeam.id <= 0) return;
    const updated = await teamApi.updatePosition(composerTeam.id, positionId, data as teamApi.UpdatePositionRequest);
    setComposerPositions(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
  }, [composerTeam]);

  // AI fill handler for position dialog – auto-creates team if needed
  const handleAIFillPosition = useCallback(async (input: { role?: string; description?: string }) => {
    const team = await ensureTeamExists();
    if (!team.id || team.id <= 0) return {};
    return teamApi.aiAutoFillPosition(team.id, input);
  }, [ensureTeamExists]);

  // AI analysis handler for position dialog – requires existing team + position
  const handleAIAnalysisPosition = useCallback(async (positionId: number) => {
    if (!composerTeam?.id || composerTeam.id <= 0) return { ai_analysis: '' };
    const result = await teamApi.generatePositionAIAnalysis(composerTeam.id, positionId);
    // Also update the position in local state
    setComposerPositions(prev => prev.map(p =>
      p.id === positionId ? { ...p, ai_analysis: result.ai_analysis } : p
    ));
    return result;
  }, [composerTeam]);

  // Add a candidate from chat (can add to specific position or unassigned)
  const handleAddCandidateFromChat = useCallback(async (
    personId: number,
    positionId?: number
  ) => {
    console.log('🎯 handleAddCandidateFromChat called:', { personId, positionId, teamId: composerTeam?.id });
    
    const person = allPeople.find(p => p.id === personId);
    if (!person) {
      console.warn('Cannot add candidate: Person not found in allPeople');
      return;
    }

    // If positionId is provided, add to that position (works for any ID including negative temp IDs)
    if (positionId !== undefined) {
      const position = composerPositions.find(p => p.id === positionId);
      if (position) {
        console.log(`✅ Adding ${person.Name} to position "${position.role}" (ID: ${positionId})`);
        
        // Calculate match scores for this candidate (three-tier scoring)
        const { score: matchScore, scores, reasoning: matchReasoning } = calculatePersonPositionMatch(person, position);
        console.log(`📊 Match scores for ${person.Name} in ${position.role}: 🧩${scores.skillMatch}% ✨${scores.semanticMatch}% 👥+${scores.teamFitBonus}%`);
        
        // Update local state immediately for responsiveness
        setComposerPositions(prev => prev.map(p => {
          if (p.id === positionId) {
            // Check if already added
            if (p.candidates?.some(c => c.person_id === personId)) {
              console.log('Candidate already in position');
              return p;
            }
            
            const newCandidate = {
              id: Date.now(),
              team_id: p.team_id,
              position_id: positionId,
              person_id: personId,
              status: 'proposed' as const, // AI suggestions need approval
              source: 'ai' as CandidateSource, // Chat-based additions
              match_score: matchScore, // Legacy field
              scores, // New three-tier scores
              match_reasoning: matchReasoning,
              added_at: new Date().toISOString(),
              profile: {
                name: person.Name,
                title: person.Title,
                team: person.Team,
                expertise: person.Expertise,
              },
            };
            
            return {
              ...p,
              candidates: [...(p.candidates || []), newCandidate],
            };
          }
          return p;
        }));
        
        // Persist to backend if team is saved and position has real ID
        if (composerTeam?.id && composerTeam.id > 0 && positionId > 0) {
          try {
            await teamApi.addCandidate(composerTeam.id, positionId, {
              person_id: personId,
              source: 'ai', // Chat-based additions use 'ai' source
              status: 'proposed', // AI suggestions need user approval
            });
          } catch (error) {
            console.debug('Failed to persist candidate to backend:', error);
          }
        }
        return;
      }
    }
    
    // No position specified or position not found - add to first available position
    if (composerPositions.length > 0) {
      const firstPosition = composerPositions[0];
      console.log(`✅ Adding ${person.Name} to first position "${firstPosition.role}"`);
      handleAddCandidateFromChat(personId, firstPosition.id);
    } else {
      console.warn('No positions available to add candidate');
    }
  }, [composerTeam, allPeople, composerPositions, calculatePersonPositionMatch]);

  // Add a candidate to a position (from drag-drop or click)
  const handleAddCandidate = useCallback(async (
    positionId: number, 
    personId: number, 
    source: CandidateSource
  ) => {
    // Find the person and position from our data
    const person = allPeople.find(p => p.id === personId);
    const position = composerPositions.find(p => p.id === positionId);
    
    // Calculate match scores if we have both person and position (three-tier scoring)
    let matchScore: number | undefined;
    let scores: { skillMatch: number; semanticMatch: number; teamFitBonus: number } | undefined;
    let matchReasoning: string | undefined;
    if (person && position) {
      const match = calculatePersonPositionMatch(person, position);
      matchScore = match.score;
      scores = match.scores;
      matchReasoning = match.reasoning;
      console.log(`📊 Match scores for ${person.Name} in ${position.role}: 🧩${scores.skillMatch}% ✨${scores.semanticMatch}% 👥+${scores.teamFitBonus}%`);
    }
    
    setComposerPositions(prev => prev.map(pos => {
      if (pos.id === positionId) {
        // Check if candidate already exists
        if (pos.candidates?.some(c => c.person_id === personId)) {
          return pos;
        }

        const newCandidate = {
          id: Date.now(),
          team_id: pos.team_id,
          position_id: positionId,
          person_id: personId,
          status: 'selected' as const, // User-added = selected
          source: source === 'drag_drop' ? 'user_selected' as const : 'user_selected' as const,
          match_score: matchScore, // Legacy field
          scores, // New three-tier scores
          match_reasoning: matchReasoning,
          added_at: new Date().toISOString(),
          profile: person ? {
            name: person.Name,
            title: person.Title,
            team: person.Team,
            expertise: person.Expertise,
          } : undefined,
        };

        return {
          ...pos,
          candidates: [...(pos.candidates || []), newCandidate],
        };
      }
      return pos;
    }));

    // Auto-create team if needed, then persist candidate
    try {
      const team = await ensureTeamExists();
      if (positionId > 0) {
        const dbSource = source === 'drag_drop' ? 'drag_drop' : 'search';
        await teamApi.addCandidate(team.id, positionId, {
          person_id: personId,
          source: dbSource as any,
          status: 'selected',
        });
      }
    } catch (error) {
      console.error('Failed to persist candidate to backend:', error);
    }
  }, [composerTeam, allPeople, composerPositions, calculatePersonPositionMatch, ensureTeamExists]);

  // Select a proposed candidate (confirm them)
  const handleSelectCandidate = useCallback(async (positionId: number, personId: number) => {
    setComposerPositions(prev => prev.map(position => {
      if (position.id === positionId) {
        return {
          ...position,
          candidates: position.candidates?.map(c => 
            c.person_id === personId ? { ...c, status: 'selected' as const } : c
          ),
        };
      }
      return position;
    }));

    // Persist to backend
    if (composerTeam?.id && composerTeam.id > 0 && positionId > 0) {
      try {
        await teamApi.selectCandidate(composerTeam.id, positionId, personId);
      } catch (error) {
        console.error('Failed to select candidate:', error);
      }
    }
  }, [composerTeam]);

  // Remove a candidate
  const handleRemoveCandidate = useCallback(async (positionId: number, personId: number) => {
    setComposerPositions(prev => prev.map(position => {
      if (position.id === positionId) {
        return {
          ...position,
          candidates: position.candidates?.filter(c => c.person_id !== personId),
        };
      }
      return position;
    }));

    // Persist to backend
    if (composerTeam?.id && composerTeam.id > 0 && positionId > 0) {
      try {
        await teamApi.removeCandidate(composerTeam.id, positionId, personId);
      } catch (error) {
        console.error('Failed to remove candidate:', error);
      }
    }
  }, [composerTeam]);

  // Reorder candidates within a position (drag up/down for priority)
  const handleReorderCandidates = useCallback((positionId: number, fromIndex: number, toIndex: number) => {
    setComposerPositions(prev => prev.map(position => {
      if (position.id === positionId && position.candidates) {
        const candidates = [...position.candidates];
        const [moved] = candidates.splice(fromIndex, 1);
        candidates.splice(toIndex, 0, moved);
        return { ...position, candidates };
      }
      return position;
    }));
    // Note: Backend persistence for candidate order can be added if needed
  }, []);

  // View candidate details (open person modal)
  const handleViewCandidate = useCallback(
    (personId: number) => {
      const person = allPeople.find((p) => p.id === personId);
      if (!person) return;
      const merged = mergePersonWithAllPeople(person);
      setSelectedPerson(merged);
      setModalSearchContext(buildModalSearchContextForPerson(merged));
    },
    [allPeople, buildModalSearchContextForPerson, mergePersonWithAllPeople]
  );

  // Open person modal from a candidate pool row. Pool members may fall outside
  // the cached `allPeople` window (LIMIT 342), so fetch on demand when missing.
  const handleOpenPersonFromPool = useCallback(
    async (personId: number) => {
      const cached = allPeople.find((p) => p.id === personId);
      if (cached) {
        setSelectedPerson(mergePersonWithAllPeople(cached));
        setModalSearchContext(null);
        return;
      }
      try {
        const fetched = await peopleApi.getById(personId);
        setSelectedPerson(fetched);
        setModalSearchContext(null);
      } catch (e) {
        console.error('Failed to load person from pool', e);
      }
    },
    [allPeople, mergePersonWithAllPeople]
  );

  // --------------------------------------------------------------------------
  // Phase 3F — open the candidate modal in review-queue mode
  // --------------------------------------------------------------------------
  /** Seed the queue + open the first candidate. Exits when queue is empty. */
  const handleStartReviewQueue = useCallback(
    async (firstPersonId: number, queue: number[]) => {
      if (queue.length === 0) return;
      const startIndex = Math.max(0, queue.indexOf(firstPersonId));
      setReviewQueue({ ids: queue, index: startIndex });
      await handleOpenPersonFromPool(firstPersonId);
    },
    [handleOpenPersonFromPool]
  );

  /**
   * Step the queue forward/backward and load the next candidate. Guards
   * against out-of-range jumps so the modal can't render a blank state.
   */
  const handleAdvanceReviewQueue = useCallback(
    async (delta: 1 | -1) => {
      setReviewQueue((q) => {
        if (!q) return q;
        const nextIndex = q.index + delta;
        if (nextIndex < 0 || nextIndex >= q.ids.length) return q;
        // Kick off async person load, but return the new state synchronously.
        handleOpenPersonFromPool(q.ids[nextIndex]).catch(() => {});
        return { ...q, index: nextIndex };
      });
    },
    [handleOpenPersonFromPool]
  );

  const handleCompleteReviewQueue = useCallback(() => {
    setReviewQueue(null);
    setSelectedPerson(null);
  }, []);

  // Create a new team from composer
  const handleCreateTeam = useCallback(async () => {
    try {
      // Collect skills from all positions (or use placeholder if none)
      const allSkills = composerPositions.flatMap(p => p.skills_required || []);
      const skills_required = allSkills.length > 0 ? allSkills : ['general'];
      
      // Create team with basic info
      const newTeam = await teamApi.createTeam({
        name: composerTeam?.name || `New Team ${new Date().toLocaleDateString()}`,
        status: 'planning',
        skills_required,
        member_count_desired: Math.max(composerPositions.reduce((sum, p) => sum + p.count_needed, 0), 1),
        description: composerTeam?.description || 'Created via Team Composer',
      });

      setComposerTeam(newTeam);

      // Now create all positions for this team
      for (const position of composerPositions) {
        if (position.id < 0) { // Only unsaved positions
          const savedPosition = await teamApi.createPosition(newTeam.id, {
            role: position.role,
            count_needed: position.count_needed,
            skills_required: position.skills_required,
            priority: position.priority,
            status: position.status,
            reasoning: position.reasoning,
            source: position.source,
          });

          // Add candidates to the position
          for (const candidate of position.candidates || []) {
            await teamApi.addCandidate(newTeam.id, savedPosition.id, {
              person_id: candidate.person_id,
              source: candidate.source === 'ai_suggested' ? 'ai_suggested' : 'user_selected',
              status: candidate.status,
              match_score: candidate.match_score,
              match_reasoning: candidate.match_reasoning,
            });
          }
        }
      }

      // Reload positions from server
      const positions = await teamApi.getTeamPositions(newTeam.id);
      setComposerPositions(positions);

      console.log('✅ Team created:', newTeam);
    } catch (error) {
      console.error('Failed to create team:', error);
    }
  }, [composerPositions, composerTeam]);

  // Save current team composition
  const handleSaveTeam = useCallback(async () => {
    if (!composerTeam?.id || composerTeam.id < 0) {
      // No team yet, create one
      await handleCreateTeam();
      return;
    }

    // Team exists, save any pending changes
    try {
      // Save unsaved positions
      for (const position of composerPositions) {
        if (position.id < 0) {
          await teamApi.createPosition(composerTeam.id, {
            role: position.role,
            count_needed: position.count_needed,
            skills_required: position.skills_required,
            priority: position.priority,
            status: position.status,
            reasoning: position.reasoning,
            source: position.source,
          });
        }
      }

      // Reload positions
      const positions = await teamApi.getTeamPositions(composerTeam.id);
      setComposerPositions(positions);

      console.log('✅ Team saved');
    } catch (error) {
      console.error('Failed to save team:', error);
    }
  }, [composerTeam, composerPositions, handleCreateTeam]);

  // Re-fetch the main people roster from the API. Called on demand after actions
  // that can add/mutate people (e.g. CV imports into candidate pools) so the main
  // browse list stays in sync without a hard reload. We only update `allPeople`;
  // `filteredPeople` is derived from it by a downstream useEffect (searchText /
  // position / AI filters) and will re-compute automatically.
  const refreshAllPeople = useCallback(async () => {
    try {
      const people = await peopleApi.getAll();
      setAllPeople(people);
    } catch (error) {
      console.error('Failed to refresh people:', error);
    }
  }, []);

  // Load initial data (only if not auth-protected OR already authenticated)
  useEffect(() => {
    // Don't load data if auth is required and user not authenticated
    if (AUTH_ENABLED && !isAuthenticated) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const people = await peopleApi.getAll();

        setAllPeople(people);
        setFilteredPeople(people);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [isAuthenticated]); // Re-run when authentication changes

  // Fetch admin status from backend (authoritative source). On 401 (expired token), clear session.
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const checkAdmin = async () => {
      try {
        const resp = await fetch(`${API_BASE}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } });
        if (clearSessionIf401(resp)) return;
        if (resp.ok) {
          const data = await resp.json();
          const ar = data.accessRole;
          const accessRole =
            ar === 'admin' || ar === 'hr' || ar === 'user' ? ar : null;
          useAuth.getState().setSessionAccess({
            isAdmin: data.isAdmin === true,
            canEditContacts: data.canEditContacts === true,
            canEditEmployeeLinkedIn: data.canEditEmployeeLinkedIn === true,
            accessRole,
          });
        }
      } catch (e) {
        console.error('Failed to verify admin status:', e);
      }
    };
    checkAdmin();
  }, [isAuthenticated, token]);

  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      if (path.startsWith('/candidate-pools')) {
        setCurrentPage('candidate_pools');
        const m = path.match(/^\/candidate-pools\/(\d+)/);
        setCandidatePoolId(m ? parseInt(m[1], 10) : null);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Re-verify token when user returns to the tab (e.g. after long idle). Clears session if expired.
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      fetch(`${API_BASE}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } })
        .then((resp) => {
          if (clearSessionIf401(resp)) return;
          if (resp.ok)
            return resp.json().then((data) => {
              const ar = data.accessRole;
              const accessRole =
                ar === 'admin' || ar === 'hr' || ar === 'user' ? ar : null;
              useAuth.getState().setSessionAccess({
                isAdmin: data.isAdmin === true,
                canEditContacts: data.canEditContacts === true,
                canEditEmployeeLinkedIn: data.canEditEmployeeLinkedIn === true,
                accessRole,
              });
            });
        })
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isAuthenticated, token]);

  // Note: Dynamic height tracking removed - using flexbox layout instead

  // Get the currently selected position for filtering
  const filterByPosition = filterByPositionId 
    ? composerPositions.find(p => p.id === filterByPositionId) 
    : null;

  const positionSkillFilterActive = Boolean(
    filterByPositionId != null &&
      filterByPosition &&
      ((filterByPosition.skills_required?.length ?? 0) > 0 ||
        (filterByPosition.skills_nice_to_have?.length ?? 0) > 0)
  );

  // Search and filter
  useEffect(() => {
    // In AI mode after at least one search completed, drive the list from AI matches (including 0 hits)
    if (shouldDriveListFromAiSearch(isAIMode, aiSearchCriteria, filterByPositionId)) {
      setFilteredPeople(aiMatches.map((m: any) => m.person));
      return;
    }

    const doSearch = () => {
      let results = [...allPeople];

      // Position-based skill filter (highest priority when active)
      // Filters AND sorts by match level (descending)
      const hasRequiredSkills = filterByPosition?.skills_required && filterByPosition.skills_required.length > 0;
      const hasNiceToHave = filterByPosition?.skills_nice_to_have && filterByPosition.skills_nice_to_have.length > 0;
      if (filterByPosition && (hasRequiredSkills || hasNiceToHave)) {
        const requiredSkills = (filterByPosition.skills_required || []).map(s => s.toLowerCase());
        const niceToHaveSkills = (filterByPosition.skills_nice_to_have || []).map(s => s.toLowerCase());
        const totalRequired = requiredSkills.length;
        
        // Calculate match score for each person and filter
        const scoredResults = results.map(person => {
          // Combine all skill-related fields
          const personSkills = [
            person.Title || '',
            person.Expertise || '',
            person.AdditionalSkills || '',
            person.AIToolExperience || '',
            person.ProfessionalInterests || '',
            person.JobResponsibilities || '',
            (person.CanonicalSkills || []).map((s) => s.name).join(' '),
          ].join(' ').toLowerCase();
          
          // Count how many required skills this person matches
          const matchedRequired = requiredSkills.filter(skill => personSkills.includes(skill));
          const requiredCount = matchedRequired.length;
          
          // Count nice-to-have matches (adds partial bonus)
          const matchedNiceToHave = niceToHaveSkills.filter(skill => personSkills.includes(skill));
          const niceToHaveBonus = niceToHaveSkills.length > 0
            ? Math.round((matchedNiceToHave.length / niceToHaveSkills.length) * 15)
            : 0;
          
          // Calculate match percentage: required skills weighted + nice-to-have bonus
          const baseScore = totalRequired > 0 ? Math.round((requiredCount / totalRequired) * 100) : 0;
          const matchScore = Math.min(100, baseScore + niceToHaveBonus);
          const totalMatchCount = requiredCount + matchedNiceToHave.length;
          
          return { person, matchScore, matchCount: totalMatchCount };
        });
        
        // Filter to only those with at least one match, then sort by score descending
        results = scoredResults
          .filter(item => item.matchCount > 0)
          .sort((a, b) => b.matchScore - a.matchScore)
          .map(item => item.person);
      }

      // Text search
      if (searchText) {
        const search = searchText.toLowerCase();
        results = results.filter(person =>
          (person.Name && person.Name.toLowerCase().includes(search)) ||
          (person.Title && person.Title.toLowerCase().includes(search)) ||
          (person.Team && person.Team.toLowerCase().includes(search)) ||
          (person.PrimaryLocation && person.PrimaryLocation.toLowerCase().includes(search)) ||
          (person.Expertise && person.Expertise.toLowerCase().includes(search)) ||
          (person.AIToolExperience && person.AIToolExperience.toLowerCase().includes(search)) ||
          (person.AdditionalSkills && person.AdditionalSkills.toLowerCase().includes(search)) ||
          (person.ProfessionalInterests && person.ProfessionalInterests.toLowerCase().includes(search)) ||
          (person.JobResponsibilities && person.JobResponsibilities.toLowerCase().includes(search)) ||
          (person.CanonicalSkills &&
            person.CanonicalSkills.some((s) => s.name.toLowerCase().includes(search)))
        );
      }

      setFilteredPeople(results);
    };

    doSearch();
  }, [searchText, allPeople, filterByPosition, filterByPositionId, isAIMode, aiMatches, aiSearchCriteria]);

  const clearBrowseSearch = () => {
    setSearchText('');
    setFilterByPositionId(null);
  };

  // Handler to clear AI context and switch back to Browse mode
  const handleClearAIContext = () => {
    if (window.confirm('Clear AI context? This will remove all search results and switch to Browse mode.')) {
      setAiMatches([]);
      setAiSearchCriteria(null);
      setAiSearchPanelBusy(false);
      setIsAIMode(false);
      setFilteredPeople(allPeople);
    }
  };

  const focusChatInput = useCallback(() => {
    setTimeout(() => chatWindowRef.current?.focusInput(), 50);
  }, []);

  const handleEnableAIMode = useCallback(() => {
    setIsAIMode(true);
    setChatMode('search');
    setChatModeLockedByUser(true);
    setShowChat(true);
    focusChatInput();
  }, [focusChatInput]);

  // Search mode keeps the workspace focused (collapsed composer, hidden team list).
  // Team-focused modes reopen composer and team list.
  useEffect(() => {
    if (chatMode === 'search') {
      setComposerExpanded(false);
      setShowLeftPanel(false);
      setLeftPanelPeeking(false);
    } else {
      setComposerExpanded(true);
      setShowLeftPanel(true);
    }
  }, [chatMode]);

  // Auto-select mode based on whether a team is in context.
  // - no team -> search mode
  // - team appears -> team mode
  useEffect(() => {
    const currentTeamId = composerTeam?.id ?? null;
    const previousTeamId = prevTeamIdRef.current;

    if (!previousTeamId && currentTeamId && !chatModeLockedByUser) {
      setChatMode('team');
      setIsAIMode(true);
    } else if (previousTeamId && !currentTeamId && !chatModeLockedByUser) {
      setChatMode('search');
      setIsAIMode(true);
      setShowChat(true);
      focusChatInput();
    }

    prevTeamIdRef.current = currentTeamId;
  }, [composerTeam?.id, focusChatInput, chatModeLockedByUser]);

  // Handler to toggle position filter
  const handleFilterByPosition = useCallback((positionId: number) => {
    setFilterByPositionId(positionId);
    setIsAIMode(true);
    setShowChat(true);
    setChatMode('candidates');
    setChatModeLockedByUser(true);
    setComposerExpanded(true);
    focusChatInput();
  }, [focusChatInput]);

  // Check if auth is required
  useEffect(() => {
    if (AUTH_ENABLED && !isAuthenticated && !netlifyUser) {
      setShowLogin(true);
    }
  }, [isAuthenticated, netlifyUser]);

  // Show login screen if auth is enabled and user not authenticated
  if (AUTH_ENABLED && showLogin && !isAuthenticated && !netlifyUser) {
    return <LoginScreen onLoginSuccess={() => setShowLogin(false)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <p className="text-dark-text-secondary text-xl">Loading…</p>
        </div>
      </div>
    );
  }

  // Admin status comes from backend via auth store (not from frontend env var)
  const isAdmin = isAdminFromStore;

  // If admin page requested, show it
  if (currentPage === 'admin') {
    return (
      <div className="min-h-screen bg-dark-bg">
        <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-dark-bg to-gray-800 -z-10"></div>
        <div className="p-4 border-b border-dark-border bg-dark-card sticky top-0 z-10">
          <div className="container mx-auto max-w-7xl flex items-center gap-4">
            <button
              onClick={() => setCurrentPage('main')}
              className="px-4 py-2 bg-brand-blue hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              ← Back to Main
            </button>
            <h1 className="text-2xl font-bold text-dark-text flex-1">⚙️ Admin Panel</h1>
          </div>
        </div>
        <div className="overflow-y-auto h-screen">
          <AdminSettings />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-dark-bg text-dark-text overflow-hidden">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-dark-bg to-gray-800 -z-10"></div>

      {/* TOP: Team Composer Bar - takes only the height it needs */}
      <div className="flex-shrink-0 z-50">
        <TeamComposerBar
          team={composerTeam}
          positions={composerPositions}
          strength={composerStrength}
          isExpanded={composerExpanded}
          onExpand={() => setComposerExpanded(true)}
          onCollapse={() => setComposerExpanded(false)}
          onAddPosition={handleAddPosition}
          onNewPosition={handleNewPosition}
          onConfirmPosition={handleConfirmPosition}
          onDeletePosition={handleDeletePosition}
          onEditPosition={handleEditPosition}
          onAddCandidate={handleAddCandidate}
          onSelectCandidate={handleSelectCandidate}
          onRemoveCandidate={handleRemoveCandidate}
          onReorderCandidates={handleReorderCandidates}
          onViewCandidate={handleViewCandidate}
          onCreatePositionFromPerson={handleCreatePositionFromPerson}
          onSave={handleSaveTeam}
          onCreate={handleCreateTeam}
          onRenameTeam={async (name) => {
            try {
              const team = await ensureTeamExists(name);
              // If team was just created with this name, we're done
              // Otherwise update existing team's name
              if (team.name !== name) {
                await teamApi.updateTeam(team.id, { name } as any);
              }
              setComposerTeam(prev => prev ? { ...prev, name } : prev);
              useProjectStore.getState().updateTeamInProject(team.id, { name });
            } catch (error) {
              console.error('Failed to rename team:', error);
            }
          }}
          showChat={showChat}
          onToggleChat={() => setShowChat(!showChat)}
          showLeftPanel={showLeftPanel || leftPanelPeeking}
          onToggleLeftPanel={() => {
            if (leftPanelPeeking) {
              // Currently peeking: pin it open
              setLeftPanelPeeking(false);
              setShowLeftPanel(true);
            } else {
              // Normal toggle
              setShowLeftPanel(!showLeftPanel);
            }
            if (peekTimerRef.current) { clearTimeout(peekTimerRef.current); peekTimerRef.current = null; }
            if (peekLeaveTimerRef.current) { clearTimeout(peekLeaveTimerRef.current); peekLeaveTimerRef.current = null; }
          }}
          isAdmin={isAdmin}
          onOpenAdmin={() => setCurrentPage('admin')}
          onOpenCandidatePools={() => {
            setCurrentPage('candidate_pools');
            setCandidatePoolId(null);
            window.history.pushState({}, '', '/candidate-pools');
          }}
          user={user || (!AUTH_ENABLED ? { name: 'John Doe', email: 'dev@localhost', picture: '' } : null)}
          onLogout={() => useAuth.getState().logout()}
          onEditTeam={async () => {
            if (composerTeam?.id && composerTeam.id > 0) {
              // Edit existing team
              try {
                const fullTeam = await teamApi.getTeam(composerTeam.id);
                setEditingTeam(fullTeam);
                setShowTeamDialog(true);
              } catch (error) {
                console.error('Failed to load team for editing:', error);
              }
            } else {
              // Create new team
              setEditingTeam(null);
              setShowTeamDialog(true);
            }
          }}
          project={composerProject ? { id: composerProject.id, name: composerProject.name } : null}
          projects={projects.map(p => ({ id: p.id, name: p.name }))}
          onSelectProject={async (projectId) => {
            if (composerTeam?.id && composerTeam.id > 0) {
              try {
                await teamApi.updateTeam(composerTeam.id, { project_id: projectId } as any);
                setComposerTeam(prev => prev ? { ...prev, project_id: projectId ?? undefined } : prev);
                setPendingProjectId(null);
                useProjectStore.getState().loadProjects();
              } catch (error) {
                console.error('Failed to assign project:', error);
              }
            } else {
              setPendingProjectId(projectId);
              if (composerTeam) {
                setComposerTeam(prev => prev ? { ...prev, project_id: projectId ?? undefined } : prev);
              }
            }
          }}
          onOpenCreateProject={() => {
            setEditingProject(null);
            setShowProjectDialog(true);
          }}
          onEditProject={(projectId) => {
            const p = projects.find(pr => pr.id === projectId);
            setEditingProject(p || { id: projectId });
            setShowProjectDialog(true);
          }}
          filterByPositionId={filterByPositionId}
          onFilterByPosition={handleFilterByPosition}
          highlightedPositionId={highlightedPositionId}
          onContentInteraction={collapseLeftPanel}
        />
      </div>
      
      {/* BOTTOM: Left Panel + Main content + Chat Sidebar */}
      <div className="flex-1 flex min-h-0 relative">
        {/* Edge hover trigger zone - thin invisible strip at left edge when panel is collapsed */}
        {!showLeftPanel && (
          <div
            className="absolute left-0 top-0 bottom-0 w-3 z-40 group"
            onMouseEnter={handleEdgeMouseEnter}
            onMouseLeave={handleEdgeMouseLeave}
          >
            {/* Subtle visual indicator line */}
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-indigo-500/0 group-hover:bg-indigo-500/40 transition-colors duration-300" />
          </div>
        )}

        {/* Left Panel - Pinned mode (in-flow, pushes content) */}
        {showLeftPanel && (
          <LeftPanel
            visible={true}
            currentTeamId={composerTeam?.id}
            onSelectTeam={async (teamId) => {
              try {
                const [teamData, positions] = await Promise.all([
                  teamApi.getTeam(teamId),
                  teamApi.getTeamPositions(teamId),
                ]);
                setComposerTeam(teamData);
                setComposerPositions(positions);
                setComposerStrength(null);
                setComposerExpanded(true);
                setPendingProjectId(null);
              } catch (error) {
                console.error('Failed to load team:', error);
              }
            }}
            onCreateTeam={() => { setEditingTeam(null); setShowTeamDialog(true); }}
            onCreateProject={() => { setEditingProject(null); setShowProjectDialog(true); }}
            onEditTeam={async (teamId) => {
              try {
                const fullTeam = await teamApi.getTeam(teamId);
                setEditingTeam(fullTeam);
                setShowTeamDialog(true);
              } catch (error) {
                console.error('Failed to load team for editing:', error);
              }
            }}
            onEditProject={(projectId) => {
              setEditingProject({ id: projectId });
              setShowProjectDialog(true);
            }}
            onShareTeam={async (teamId) => {
              try {
                const t = await teamApi.getTeam(teamId);
                setShareTeamId(teamId);
                setShareTeamName(t.name);
              } catch (e) {
                console.error('Failed to load team for sharing:', e);
              }
            }}
            onShareProject={() => {}}
          />
        )}

        {/* Left Panel - Peek mode (overlay, floats over content) */}
        {!showLeftPanel && leftPanelPeeking && (
          <div
            className="absolute left-0 top-0 bottom-0 z-50 animate-slide-in-left"
            style={{ boxShadow: '4px 0 24px rgba(0,0,0,0.5)' }}
            onMouseEnter={handlePeekPanelEnter}
            onMouseLeave={handlePeekPanelLeave}
          >
            <LeftPanel
              visible={true}
              currentTeamId={composerTeam?.id}
              onSelectTeam={async (teamId) => {
                try {
                  const [teamData, positions] = await Promise.all([
                    teamApi.getTeam(teamId),
                    teamApi.getTeamPositions(teamId),
                  ]);
                  setComposerTeam(teamData);
                  setComposerPositions(positions);
                  setComposerStrength(null);
                  setComposerExpanded(true);
                  setPendingProjectId(null);
                  setLeftPanelPeeking(false); // Close peek after selecting
                } catch (error) {
                  console.error('Failed to load team:', error);
                }
              }}
              onCreateTeam={() => { setEditingTeam(null); setShowTeamDialog(true); setLeftPanelPeeking(false); }}
              onCreateProject={() => { setEditingProject(null); setShowProjectDialog(true); setLeftPanelPeeking(false); }}
              onEditTeam={async (teamId) => {
                try {
                  const fullTeam = await teamApi.getTeam(teamId);
                  setEditingTeam(fullTeam);
                  setShowTeamDialog(true);
                  setLeftPanelPeeking(false);
                } catch (error) {
                  console.error('Failed to load team for editing:', error);
                }
              }}
              onEditProject={(projectId) => {
                setEditingProject({ id: projectId });
                setShowProjectDialog(true);
                setLeftPanelPeeking(false);
              }}
              onShareTeam={async (teamId) => {
                try {
                  const t = await teamApi.getTeam(teamId);
                  setShareTeamId(teamId);
                  setShareTeamName(t.name);
                } catch (e) {
                  console.error('Failed to load team for sharing:', e);
                }
              }}
              onShareProject={() => {}}
            />
          </div>
        )}

        {/* Main Content - fills remaining width, scrollable */}
        <main className="flex-1 min-h-0 overflow-y-auto flex flex-col" onClick={collapseLeftPanel}>
          {currentPage === 'candidate_pools' ? (
            <CandidatePoolsPage
              embedded
              initialPoolId={candidatePoolId}
              onNavigatePool={(id) => {
                setCandidatePoolId(id);
                if (id == null) {
                  window.history.pushState({}, '', '/candidate-pools');
                } else {
                  window.history.pushState({}, '', `/candidate-pools/${id}`);
                }
              }}
              onBack={() => {
                setCurrentPage('main');
                setCandidatePoolId(null);
                window.history.pushState({}, '', '/');
              }}
              currentUserEmail={user?.email || ''}
              onNavigatePerson={handleOpenPersonFromPool}
              onPeopleChanged={refreshAllPeople}
              onStartReviewQueue={handleStartReviewQueue}
              onRegisterPoolRefresh={(fn) => {
                candidatePoolRefreshRef.current = fn;
              }}
            />
          ) : (
          <div className="container mx-auto p-6 max-w-7xl animate-fade-in">
            {/* Search and content area */}

            {/* Search and Filters */}
            <div className="mb-6">
              {/* View Mode Toggle + Filter Toggle Row */}
              <div className="flex flex-wrap items-center gap-4 mb-4 min-w-0">
                {/* View Mode Toggle */}
                <div className="inline-flex rounded-xl bg-dark-card border border-dark-border p-1">
                  <button
                    onClick={() => setIsAIMode(false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      !isAIMode
                        ? 'bg-brand-blue text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    🗂️ Browse
                  </button>
                  <button
                    onClick={handleEnableAIMode}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isAIMode
                        ? 'bg-brand-blue text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    ✨ AI
                    {aiSearchCriteria != null && (
                      <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                        {aiMatches.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Clear AI Context — after any completed AI search (including zero matches) */}
                {aiSearchCriteria != null && isAIMode && (
                  <button
                    onClick={handleClearAIContext}
                    className="text-sm text-gray-400 hover:text-red-400 transition-colors flex items-center gap-1"
                    title="Clear AI context and switch to Browse"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Clear context
                  </button>
                )}

                {/* Browse search — single field (name, title, team, skills, location, etc.) */}
                {!isAIMode && (
                  <div className="flex flex-1 min-w-0 items-center gap-3">
                    <label htmlFor="browse-people-search" className="sr-only">
                      Search people
                    </label>
                    <input
                      id="browse-people-search"
                      data-testid="browse-search"
                      type="search"
                      autoComplete="off"
                      placeholder="Search people by name, title, team, skills, location…"
                      className="flex-1 min-w-0 px-4 py-3 bg-dark-card border border-dark-border rounded-xl text-dark-text placeholder-dark-text-secondary text-base focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue/50"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <span
                      className="shrink-0 tabular-nums text-sm font-medium text-dark-text-secondary px-3 py-2 rounded-xl bg-dark-bg border border-dark-border"
                      aria-live="polite"
                    >
                      {filteredPeople.length}/{allPeople.length}
                    </span>
                  </div>
                )}
              </div>

              {/* Position Filter Indicator - shown when filtering by a position */}
              {filterByPosition && (
                <div className="bg-cyan-900/30 border border-cyan-500/50 rounded-xl px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <div>
                      <span className="text-cyan-300 font-medium">
                        Filtering for: {filterByPosition.role}
                      </span>
                      {filterByPosition.skills_required && filterByPosition.skills_required.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {filterByPosition.skills_required.map((skill, idx) => (
                            <span key={idx} className="text-xs px-2 py-0.5 bg-cyan-800/50 text-cyan-300 rounded">
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setFilterByPositionId(null)}
                    className="px-3 py-1.5 bg-cyan-600/30 hover:bg-cyan-600/50 text-cyan-300 rounded-lg text-sm transition-colors flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Clear Filter
                  </button>
                </div>
              )}

              {/* AI Search Criteria - shown in AI mode when criteria exist */}
              {isAIMode && aiSearchCriteria && (
                <AISearchCriteriaBar
                  criteria={aiSearchCriteria}
                  resultCount={aiMatches.length}
                  isSearchRefreshing={aiSearchPanelBusy}
                />
              )}

            </div>

            {/* People List or Grid */}
            {isAIMode ? (
              // AI MODE — position skill filter shows browse-style cards (not chat aiMatches)
              positionSkillFilterActive && filteredPeople.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredPeople.map((person, index) => (
                    <PersonCard
                      key={person.id ?? index}
                      person={person}
                      onClick={() => {
                        const merged = mergePersonWithAllPeople(person);
                        setSelectedPerson(merged);
                        setModalSearchContext(buildModalSearchContextForPerson(merged));
                      }}
                    />
                  ))}
                </div>
              ) : positionSkillFilterActive && !aiSearchPanelBusy && filteredPeople.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-xl font-semibold text-dark-text mb-2">No people matched this position</h3>
                  <p className="text-dark-text-secondary mb-4 max-w-md mx-auto">
                    No profiles matched the required or nice-to-have skills for this role. Try clearing the filter or
                    refining the position skills.
                  </p>
                  <button
                    type="button"
                    onClick={() => setFilterByPositionId(null)}
                    className="px-6 py-3 bg-brand-blue hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                  >
                    Clear position filter
                  </button>
                </div>
              ) : aiMatches.length > 0 ? (
                // AI MODE with results: Show as list with detailed analysis
                <div
                  className={`space-y-2 transition-opacity duration-200 ${aiSearchPanelBusy ? 'opacity-55 pointer-events-none' : ''}`}
                  aria-busy={aiSearchPanelBusy}
                >
                  {filteredPeople.map((person, index) => {
                    const aiMatch = aiMatches.find(m => m.person?.Name === person.Name);
                    return (
                      <AIResultCard
                        key={index}
                        person={person}
                        matchScore={aiMatch?.match_score}
                        scores={aiMatch?.scores}
                        searchContextRole={aiSearchCriteria?.extracted?.role ?? null}
                        aiReasoning={aiMatch?.ai_reasoning}
                        strengths={aiMatch?.strengths}
                        searchInsight={aiMatch?.semantic_insight}
                        onClick={() => {
                          const merged = mergePersonWithAllPeople(person);
                          setSelectedPerson(merged);
                          setModalSearchContext(buildModalSearchContextForPerson(merged));
                        }}
                        onDragStart={() => setDraggedPerson(person)}
                      />
                    );
                  })}
                </div>
              ) : aiSearchPanelBusy ? (
                <div
                  className="text-center py-16"
                  role="status"
                  aria-live="polite"
                  aria-busy="true"
                  data-testid="ai-search-working"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-blue/15 border border-brand-blue/30 mb-4">
                    <svg
                      className="w-8 h-8 text-brand-blue animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-90"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-dark-text mb-2">Working on your search</h3>
                  <p className="text-dark-text-secondary max-w-md mx-auto">
                    Matching people to your chat criteria can take a little while. Results will appear here when ready.
                  </p>
                </div>
              ) : aiSearchCriteria ? (
                // AI MODE with completed search but no matches
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">🔎</div>
                  <h3 className="text-xl font-semibold text-dark-text mb-2">No matches found</h3>
                  <p className="text-dark-text-secondary mb-4">
                    Try broadening the query or adding alternative keywords in chat.
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={focusChatInput}
                      className="px-4 py-2 bg-brand-blue/20 border border-brand-blue/40 text-brand-blue rounded-lg hover:bg-brand-blue/30 transition-colors"
                    >
                      Focus chat
                    </button>
                    <button
                      onClick={() => setIsAIMode(false)}
                      className="px-4 py-2 text-brand-blue hover:underline"
                    >
                      Switch to Browse mode
                    </button>
                  </div>
                </div>
              ) : (
                // AI MODE without any search yet: Initial empty state
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">✨</div>
                  <h3 className="text-xl font-semibold text-dark-text mb-2">Start search from chat</h3>
                  <p className="text-dark-text-secondary mb-4">
                    Use the chat panel on the right to search people with AI.
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={focusChatInput}
                      className="px-4 py-2 bg-brand-blue/20 border border-brand-blue/40 text-brand-blue rounded-lg hover:bg-brand-blue/30 transition-colors"
                    >
                      Focus chat
                    </button>
                    <button
                      onClick={() => setIsAIMode(false)}
                      className="px-4 py-2 text-brand-blue hover:underline"
                    >
                      Switch to Browse mode
                    </button>
                  </div>
                </div>
              )
            ) : (
              // BROWSE MODE
              filteredPeople.length > 0 ? (
                // BROWSE MODE with results: Show as grid
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredPeople.map((person, index) => (
                    <PersonCard
                      key={index}
                      person={person}
                      onClick={() => {
                        const merged = mergePersonWithAllPeople(person);
                        setSelectedPerson(merged);
                        setModalSearchContext(buildModalSearchContextForPerson(merged));
                      }}
                    />
                  ))}
                </div>
              ) : (
                // BROWSE MODE without results: No people found
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-xl font-semibold text-dark-text mb-2">No people found</h3>
                  <p className="text-dark-text-secondary mb-4">Try a different search term</p>
                  <button
                    type="button"
                    onClick={clearBrowseSearch}
                    className="px-6 py-3 bg-brand-blue hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                  >
                    Clear search
                  </button>
                </div>
              )
            )}

          </div>
          )}
          {selectedPerson && (
            <CandidateProfileModal
              person={selectedPerson}
              viewerContext={{ kind: 'admin' }}
              onClose={() => {
                setSelectedPerson(null);
                setModalSearchContext(null);
                setReviewQueue(null);
              }}
              onNavigateToCandidatePool={(poolId) => {
                setSelectedPerson(null);
                setModalSearchContext(null);
                setReviewQueue(null);
                window.history.pushState({}, '', `/candidate-pools/${poolId}`);
                setCurrentPage('candidate_pools');
                setCandidatePoolId(poolId);
              }}
              reviewQueue={
                reviewQueue
                  ? {
                      ids: reviewQueue.ids,
                      index: reviewQueue.index,
                      onPrev: () => handleAdvanceReviewQueue(-1),
                      onNext: () => handleAdvanceReviewQueue(1),
                      onComplete: handleCompleteReviewQueue,
                    }
                  : undefined
              }
              onReviewStatusChanged={() => {
                candidatePoolRefreshRef.current?.();
                refreshAllPeople();
              }}
              onPersonUpdated={(updated) => {
                setAllPeople((prev) =>
                  prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
                );
                setFilteredPeople((prev) =>
                  prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
                );
                setSelectedPerson((prev) =>
                  prev && prev.id === updated.id ? { ...prev, ...updated } : prev
                );
                candidatePoolRefreshRef.current?.();
              }}
              onArchived={() => {
                const archivedId = selectedPerson?.id;
                refreshAllPeople();
                if (archivedId != null) {
                  setAllPeople((prev) => prev.filter((p) => p.id !== archivedId));
                  setFilteredPeople((prev) => prev.filter((p) => p.id !== archivedId));
                }
                setSelectedPerson(null);
                setModalSearchContext(null);
                setReviewQueue(null);
                candidatePoolRefreshRef.current?.();
              }}
              isAdmin={isAdmin}
            />
          )}
        </main>

        {/* AI Chat Sidebar - fixed width, fully hidden when toggled off */}
        {showChat && (
        <aside 
          className="w-96 flex-shrink-0 bg-dark-bg border-l border-dark-border shadow-2xl overflow-y-auto"
          onClick={collapseLeftPanel}
        >
          <div className="w-96 h-full">
            <ChatWindow 
              ref={chatWindowRef}
              mode={chatMode}
              onModeChange={(mode) => {
                setChatMode(mode);
                setChatModeLockedByUser(true);
              }}
              onSearchPanelBusyChange={setAiSearchPanelBusy}
              onResults={(matches, criteria) => {
                setAiMatches(matches);
                setAiSearchCriteria((criteria as any) || null);
                setIsAIMode(true);
                setFilteredPeople(matches.map((m: any) => m.person));
              }}
              // Team Composer context for chat awareness
              composerContext={{
                team: composerTeam,
                positions: composerPositions,
                candidates: composerPositions.flatMap(p => p.candidates || []),
              }}
              // Team Composer SSE event handlers
              onTeamCreated={handleTeamCreated}
              onTeamSelected={handleTeamSelected}
              onPositionsSuggested={handlePositionsSuggested}
              onCandidatesMatched={handleCandidatesMatched}
              onTeamStrengthUpdate={handleTeamStrengthUpdate}
              // Handler to add candidates from chat inline cards
              onAddCandidateToTeam={handleAddCandidateFromChat}
              // Handler to add positions from chat suggestion buttons
              onAddPositionToTeam={async (position) => {
                try {
                  // If no saved team, create one first
                  let teamId = composerTeam?.id;
                  if (!teamId || teamId <= 0) {
                    const newTeam = await teamApi.createTeam({
                      name: composerTeam?.name || `Team ${new Date().toLocaleDateString()}`,
                      status: 'planning',
                    });
                    teamId = newTeam.id;
                    setComposerTeam(newTeam);
                    useProjectStore.getState().loadProjects();
                    console.log(`Auto-created team: ${newTeam.name} (ID: ${newTeam.id})`);
                  }

                  const savedPosition = await teamApi.createPosition(teamId, {
                    role: position.role,
                    count_needed: position.count_needed || 1,
                    skills_required: position.skills_required || [],
                    priority: position.priority || 'important',
                    status: 'confirmed',
                    source: 'ai',
                    reasoning: position.reasoning,
                  });
                  setComposerPositions(prev => [...prev, savedPosition]);
                  setComposerExpanded(true);
                  console.log(`Added position from chat: ${savedPosition.role} (ID: ${savedPosition.id})`);
                } catch (error) {
                  console.error('Failed to add position from chat:', error);
                }
              }}
              // Handler to highlight position when chat searches for candidates
              onPositionHighlight={handlePositionHighlight}
            />
          </div>
        </aside>
        )}
      </div>

      {/* Team Create/Edit Dialog */}
      {showTeamDialog && (
        <TeamDialog
          team={editingTeam}
          defaultProjectId={editingTeam ? undefined : (composerTeam?.project_id ?? pendingProjectId ?? undefined)}
          onSave={async (data) => {
            try {
              if (editingTeam?.id) {
                await teamApi.updateTeam(editingTeam.id, {
                  name: data.name,
                  project_id: data.project_id,
                  status: data.status,
                  team_type: data.team_type,
                  start_date: data.start_date,
                  end_date: data.end_date,
                  location: data.location,
                  location_model: data.location_model,
                  expected_deliverables: data.expected_deliverables,
                  success_criteria: data.success_criteria,
                } as any);
              } else {
                const newTeam = await teamApi.createTeam({
                  name: data.name,
                  project_id: data.project_id,
                  status: 'planning',
                  team_type: data.team_type,
                  start_date: data.start_date,
                  end_date: data.end_date,
                  location: data.location,
                  location_model: data.location_model,
                  expected_deliverables: data.expected_deliverables,
                  success_criteria: data.success_criteria,
                });
                // Set as current team in composer
                setComposerTeam(newTeam);
                setComposerPositions([]);
                setComposerStrength(null);
                setComposerExpanded(true);
              }
              setShowTeamDialog(false);
              setEditingTeam(null);
              // Reload projects to reflect changes in left panel
              useProjectStore.getState().loadProjects();
            } catch (error) {
              console.error('Failed to save team:', error);
            }
          }}
          onClose={() => { setShowTeamDialog(false); setEditingTeam(null); }}
        />
      )}

      {/* Share Team Modal */}
      {shareTeamId !== null && (
        <ShareTeamModal
          teamId={shareTeamId}
          teamName={shareTeamName}
          onClose={() => { setShareTeamId(null); setShareTeamName(''); }}
          onSuccess={() => {}}
        />
      )}

      {/* Project Create/Edit Dialog */}
      {showProjectDialog && (
        <ProjectDialog
          project={editingProject}
          onSave={async (data) => {
            try {
              const projectStore = useProjectStore.getState();
              if (editingProject?.id) {
                await projectStore.updateProject(editingProject.id, data);
              } else {
                const newProject = await projectStore.createProject(data as any);
                if (composerTeam?.id && composerTeam.id > 0) {
                  await teamApi.updateTeam(composerTeam.id, { project_id: newProject.id } as any);
                  setComposerTeam(prev => prev ? { ...prev, project_id: newProject.id } : prev);
                  setPendingProjectId(null);
                } else {
                  setPendingProjectId(newProject.id);
                  if (composerTeam) {
                    setComposerTeam(prev => prev ? { ...prev, project_id: newProject.id } : prev);
                  }
                }
                useProjectStore.getState().loadProjects();
              }
              setShowProjectDialog(false);
              setEditingProject(null);
            } catch (error) {
              console.error('Failed to save project:', error);
            }
          }}
          onClose={() => { setShowProjectDialog(false); setEditingProject(null); }}
        />
      )}

      {/* Position Edit Dialog (Slide-over) */}
      {showPositionDialog && (
        <PositionEditDialog
          position={editingPositionId ? composerPositions.find(p => p.id === editingPositionId) || null : null}
          teamId={composerTeam?.id || 0}
          ensureTeamId={async () => { const t = await ensureTeamExists(); return t.id; }}
          onCreate={handleCreatePosition}
          onUpdate={handleUpdatePosition}
          onClose={() => { setShowPositionDialog(false); setEditingPositionId(null); }}
          onAIFill={handleAIFillPosition}
          onAIAnalysis={handleAIAnalysisPosition}
          onInfographicGenerated={(posId) => {
            setComposerPositions(prev => prev.map(p =>
              p.id === posId ? { ...p, infographic_base64: 'generated' } : p
            ));
          }}
        />
      )}
      <Footer />
    </div>
  );
}

export default App;


